import React, { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  CalendarDaysIcon,
  CheckBadgeIcon,
  DevicePhoneMobileIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const DISMISS_KEY = 'smileconsent-demo-popup-dismissed';

function isReactSnap() {
  return typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';
}

const POINTS = [
  {
    icon: CalendarDaysIcon,
    title: 'Personalised demo',
    body: 'See SmileConsent on a real consent',
  },
  {
    icon: DevicePhoneMobileIcon,
    title: 'Signed on their phone',
    body: 'From draft to signature in one flow',
  },
  {
    icon: CheckBadgeIcon,
    title: 'Clinician in control',
    body: 'You review the wording before it sends',
  },
];

export default function DemoLeadPopup() {
  const [open, setOpen] = useState(false);
  const closeRef = useRef(null);
  const titleId = useId();

  useEffect(() => {
    if (isReactSnap()) return undefined;
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === '1') return undefined;
    } catch {
      /* show anyway */
    }
    const timer = window.setTimeout(() => setOpen(true), 900);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKey = (event) => {
      if (event.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div className="hpv2-popup" role="presentation">
      <button type="button" className="hpv2-popup-backdrop" aria-label="Close demo offer" onClick={dismiss} />
      <div
        className="hpv2-popup-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <button
          ref={closeRef}
          type="button"
          className="hpv2-popup-close"
          onClick={dismiss}
          aria-label="Close"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <div className="hpv2-popup-copy">
          <p className="hpv2-popup-brand">
            <span aria-hidden="true">✦</span> SmileConsent
          </p>
          <h2 id={titleId}>
            See consent
            <span> in the room</span>
          </h2>
          <p className="hpv2-popup-lead">
            Book a short demo and we’ll walk a UK consent from draft to signature, so you can see what your team would actually use.
          </p>

          <ul className="hpv2-popup-points">
            {POINTS.map(({ icon: Icon, title, body }) => (
              <li key={title}>
                <span>
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <strong>{title}</strong>
                <em>{body}</em>
              </li>
            ))}
          </ul>

          <Link to="/book-demo" className="hpv2-popup-cta" onClick={dismiss}>
            Book a demo
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
          </Link>
          <p className="hpv2-popup-note">Takes less than 30 minutes</p>
        </div>

        <div className="hpv2-popup-visual" aria-hidden="true">
          <div className="hpv2-popup-window">
            <div className="hpv2-popup-chrome">
              <span />
              <span />
              <span />
              <p>Consents</p>
            </div>
            <div className="hpv2-popup-rows">
              <div>
                <b>A. Khan</b>
                <i className="is-signed">Signed</i>
              </div>
              <div>
                <b>M. Ellis</b>
                <i className="is-sent">Sent</i>
              </div>
              <div>
                <b>R. Cole</b>
                <i className="is-wait">Pending</i>
              </div>
              <div>
                <b>S. Patel</b>
                <i className="is-ask">Questions</i>
              </div>
            </div>
          </div>
          <div className="hpv2-popup-gift">
            <strong>Free</strong>
            <span>live demo</span>
          </div>
        </div>
      </div>
    </div>
  );
}
